import { useState, useMemo, useEffect, useRef } from 'react';

import Alert from '../../../alert';

import { stripParentheses } from '../../../../../utils';

import { decisionTypes } from './data';
import {
  principlesDecisionTypeCards,
  principlesRecommendedByLevel,
  principlesModalCopy,
  principlesTheme,
} from './principlesModalData';

// Reuse the BE modal stylesheet — class names + animations are
// identical; the only theming differences (card bg/border/divider/
// text) are exposed as CSS variables overridden by `principlesTheme`
// on the modal root.
import './balancedEquationLevelModal.css';

const STEP_DECISION = 'decision';
const STEP_SCENARIO = 'scenario';

// Stable empty-array reference — see BE modal for explanation.
const EMPTY_PRECHECKED = [];

// 2-step add-level flow for The Principles. Mirrors the Balanced
// Equation modal in structure and behavior; differs only in card
// copy, card color theme, and the source decisionTypes lookup.
//
// Props match BalancedEquationLevelModal so add.js can swap based on
// gameType without changing handler signatures.
export default function PrinciplesLevelModal({
  scenarioLevelNumber,
  filterItems,
  isButtonLoading,
  onCloseButtonClick,
  onSaveButtonClick,
  decisionType = '',
  editingScenarioLevelPreCheckedItems = EMPTY_PRECHECKED,
}) {
  const gameType = 'The Principles';
  const labelByKey = decisionTypes[gameType] || {};
  const isEditMode = !!decisionType;

  const cards = useMemo(() => principlesDecisionTypeCards.filter((c) => !c.hidden), []);

  const [step, setStep] = useState(isEditMode ? STEP_SCENARIO : STEP_DECISION);
  const [leavingStep, setLeavingStep] = useState(null);
  const [selectedDecisionTypeKey, setSelectedDecisionTypeKey] = useState(
    isEditMode ? decisionType : null
  );
  const [selectedScenarioName, setSelectedScenarioName] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [pendingScenarioName, setPendingScenarioName] = useState(null);

  const editInitPendingRef = useRef(isEditMode);

  const recommendedKey = principlesRecommendedByLevel[scenarioLevelNumber] || null;

  const selectedCard = useMemo(
    () => cards.find((c) => c.key === selectedDecisionTypeKey) || null,
    [cards, selectedDecisionTypeKey]
  );

  const decisionTypeLabel = selectedDecisionTypeKey ? labelByKey[selectedDecisionTypeKey] : null;
  const scenarioBucket = decisionTypeLabel ? filterItems?.[decisionTypeLabel] : null;
  const scenarioNames = useMemo(
    () => (scenarioBucket ? Object.keys(scenarioBucket) : []),
    [scenarioBucket]
  );

  const initialEditScenarioName = useMemo(() => {
    if (!isEditMode || !scenarioBucket || !editingScenarioLevelPreCheckedItems.length) {
      return null;
    }
    const precheckedTexts = new Set(
      editingScenarioLevelPreCheckedItems.map((p) => p.text)
    );
    let best = null;
    let bestScore = 0;
    Object.entries(scenarioBucket).forEach(([name, items]) => {
      const score = items.filter((it) => precheckedTexts.has(it.text)).length;
      if (score > bestScore) {
        best = name;
        bestScore = score;
      }
    });
    return best;
  }, [isEditMode, scenarioBucket, editingScenarioLevelPreCheckedItems]);

  useEffect(() => {
    if (step === STEP_SCENARIO && !selectedScenarioName && scenarioNames.length) {
      setSelectedScenarioName(initialEditScenarioName || scenarioNames[0]);
    }
    if (step === STEP_DECISION) {
      setSelectedScenarioName(null);
      setSelectedTransactions([]);
    }
  }, [step, scenarioNames, selectedScenarioName, initialEditScenarioName]);

  const visibleTransactions = useMemo(() => {
    if (!scenarioBucket || !selectedScenarioName) return [];
    return scenarioBucket[selectedScenarioName] || [];
  }, [scenarioBucket, selectedScenarioName]);

  // visibleTransactions intentionally omitted from deps — see BE
  // modal for the explanation; including it clobbers user unchecks
  // on every parent re-render.
  useEffect(() => {
    if (step !== STEP_SCENARIO || !selectedScenarioName) return;

    if (editInitPendingRef.current && editingScenarioLevelPreCheckedItems.length) {
      const precheckedTexts = new Set(
        editingScenarioLevelPreCheckedItems.map((p) => p.text)
      );
      setSelectedTransactions(
        visibleTransactions.filter((t) => precheckedTexts.has(t.text))
      );
      editInitPendingRef.current = false;
      return;
    }

    setSelectedTransactions(visibleTransactions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedScenarioName, editingScenarioLevelPreCheckedItems]);

  const handleCardClick = (key) => {
    if (step !== STEP_DECISION) return;
    setSelectedDecisionTypeKey(key);
    setStep(STEP_SCENARIO);
  };

  const handleChangeDecisionType = () => {
    setLeavingStep(STEP_SCENARIO);
    setTimeout(() => {
      setStep(STEP_DECISION);
      setSelectedDecisionTypeKey(null);
      setSelectedScenarioName(null);
      setSelectedTransactions([]);
      setLeavingStep(null);
    }, 220);
  };

  const handleScenarioRadioClick = (name) => {
    if (name === selectedScenarioName) return;

    const hasCustomized =
      selectedTransactions.length !== visibleTransactions.length ||
      !visibleTransactions.every((t) =>
        selectedTransactions.some((s) => s.id === t.id)
      );

    if (hasCustomized && selectedTransactions.length > 0) {
      setPendingScenarioName(name);
      return;
    }
    setSelectedScenarioName(name);
  };

  const handleScenarioSwitchConfirm = () => {
    setSelectedScenarioName(pendingScenarioName);
    setSelectedTransactions([]);
    setPendingScenarioName(null);
  };

  const handleScenarioSwitchCancel = () => {
    setPendingScenarioName(null);
  };

  const handleTransactionToggle = (item, isChecked) => {
    setSelectedTransactions((prev) => {
      if (isChecked) {
        if (prev.some((t) => t.id === item.id)) return prev;
        return [...prev, item];
      }
      return prev.filter((t) => t.id !== item.id);
    });
  };

  // Bulk select / deselect toggle — operates on the currently-visible
  // list only. See balancedEquationLevelModal.js for the full rationale.
  const areAllVisibleSelected =
    visibleTransactions.length > 0
    && visibleTransactions.every((t) => selectedTransactions.some((s) => s.id === t.id));
  const areNoneVisibleSelected =
    visibleTransactions.length > 0
    && !visibleTransactions.some((t) => selectedTransactions.some((s) => s.id === t.id));

  // Sticky label: the toggle only flips when the user clicks it OR
  // when the visible selection hits a boundary (all → 'deselect',
  // none → 'select'). A single incidental uncheck no longer swaps
  // the label out from under the user.
  const [selectAllMode, setSelectAllMode] = useState('select');
  useEffect(() => {
    if (areAllVisibleSelected) setSelectAllMode('deselect');
    else if (areNoneVisibleSelected) setSelectAllMode('select');
  }, [areAllVisibleSelected, areNoneVisibleSelected]);

  const handleToggleAllTransactions = () => {
    if (selectAllMode === 'deselect') {
      const visibleIds = new Set(visibleTransactions.map((t) => t.id));
      setSelectedTransactions((prev) => prev.filter((t) => !visibleIds.has(t.id)));
    } else {
      setSelectedTransactions((prev) => {
        const merged = [...prev];
        visibleTransactions.forEach((t) => {
          if (!merged.some((s) => s.id === t.id)) merged.push(t);
        });
        return merged;
      });
    }
  };

  const handleSaveClick = () => {
    if (!selectedTransactions.length) return;
    onSaveButtonClick(
      selectedTransactions,
      selectedScenarioName,
      decisionTypeLabel,
      onCloseButtonClick,
      gameType,
      selectedDecisionTypeKey
    );
  };

  const renderCard = (card, opts = {}) => {
    const { pinned = false, allowClick = true, footer = null } = opts;
    const isRecommended = card.key === recommendedKey;
    const isSelected = card.key === selectedDecisionTypeKey;
    return (
      <div
        key={card.key}
        className={`be-level-card${pinned ? ' be-level-modal-pinned-card' : ''}`}
        data-selected={isSelected ? 'true' : 'false'}
        onClick={allowClick && !pinned ? () => handleCardClick(card.key) : undefined}
        role={allowClick && !pinned ? 'button' : undefined}
        tabIndex={allowClick && !pinned ? 0 : undefined}
      >
        <h3 className="be-level-card-title">{card.title}</h3>
        <hr className="be-level-card-divider" />
        <p className="be-level-card-description">
          {card.description.map((seg, i) =>
            seg.bold ? <b key={i}>{seg.text}</b> : <span key={i}>{seg.text}</span>
          )}
        </p>
        {isRecommended && !pinned && (
          <div className="be-level-card-recommended">
            {principlesModalCopy.recommendedBadge.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        )}
        {footer}
      </div>
    );
  };

  return (
    <div className="be-level-modal-overlay">
      <div
        className="be-level-modal"
        data-step={step}
        data-leaving={leavingStep || undefined}
        data-game="principles"
        style={principlesTheme}
      >
        <button
          className="be-level-modal-close"
          onClick={onCloseButtonClick}
          aria-label="Close"
        >
          ×
        </button>

        <div className="be-level-modal-header">
          <h2 className="be-level-modal-title">
            {step === STEP_DECISION
              ? principlesModalCopy.title(scenarioLevelNumber)
              : principlesModalCopy.step2Title(scenarioLevelNumber)}
          </h2>
          <p className="be-level-modal-subtitle">
            {step === STEP_DECISION
              ? principlesModalCopy.subtitle
              : principlesModalCopy.step2Subtitle}
          </p>
        </div>

        {step === STEP_DECISION && (
          <div className="be-level-modal-cards">
            {cards.map((card) => renderCard(card))}
          </div>
        )}

        {step === STEP_SCENARIO && selectedCard && (
          <div className="be-level-modal-step2">
            {renderCard(selectedCard, {
              pinned: true,
              allowClick: false,
              footer: isEditMode ? null : (
                <button
                  type="button"
                  className="be-level-modal-back-button be-level-modal-pinned-back-button"
                  onClick={handleChangeDecisionType}
                >
                  {principlesModalCopy.changeDecisionTypeButton}
                </button>
              ),
            })}

            <div className="be-level-modal-step2-columns">
              <div>
                <h3 className="be-level-modal-column-header">
                  {principlesModalCopy.scenariosColumnHeader}
                </h3>
                <div className="be-level-modal-scenarios">
                  {scenarioNames.map((name) => {
                    const active = name === selectedScenarioName;
                    return (
                      <label
                        key={name}
                        className={`be-level-modal-scenario-option${active ? ' be-level-modal-scenario-option--active' : ''}`}
                      >
                        <input
                          type="radio"
                          name="principles-business-scenario"
                          checked={active}
                          onChange={() => handleScenarioRadioClick(name)}
                        />
                        <span>{stripParentheses(name)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="be-level-modal-column-header-row">
                  <h3 className="be-level-modal-column-header">
                    {principlesModalCopy.transactionsColumnHeader}
                  </h3>
                  {visibleTransactions.length > 0 && (
                    <button
                      type="button"
                      className="be-level-modal-toggle-all"
                      onClick={handleToggleAllTransactions}
                    >
                      {selectAllMode === 'deselect' ? 'Deselect all' : 'Select all'}
                    </button>
                  )}
                </div>
                <div className="be-level-modal-transactions">
                  {visibleTransactions.map((item) => {
                    const checked = selectedTransactions.some((t) => t.id === item.id);
                    return (
                      <label key={item.id} className="be-level-modal-transaction-row">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => handleTransactionToggle(item, e.target.checked)}
                        />
                        <span>{item.text}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="be-level-modal-footer">
          <span />
          {step === STEP_SCENARIO && (
            <button
              type="button"
              className={`button-blue${isButtonLoading ? ' loading' : ''}`}
              disabled={!selectedTransactions.length || isButtonLoading}
              onClick={handleSaveClick}
            >
              {principlesModalCopy.saveButton}
            </button>
          )}
        </div>
      </div>

      {pendingScenarioName && (
        <Alert
          type="warning"
          title={principlesModalCopy.switchScenarioWarningTitle}
          text={principlesModalCopy.switchScenarioWarningText}
          confirmButtonText="Yes"
          cancelButtonText="No"
          onConfirmButtonClick={handleScenarioSwitchConfirm}
          onCancelButtonClick={handleScenarioSwitchCancel}
        />
      )}
    </div>
  );
}
